// Comms final pass (1.12.217-beta): Gated Group Communications.
// When the company setting is ON, the first staff member to engage a conversation (send, call, or Take) owns it; everyone else
// reads it and must ask to join, be invited, or wait for a release. The rule lives in the database (migration 284:
// conv_gate_claim_send / conv_gate_status / conv_release / conv_gate_timeline). This file is shared by the screen and by the
// Edge Functions that send for staff, so both say the same words.

export const GATE_OWNED_CODE = 'conversation_owned';
export const GATED_SETTING_LABEL = 'Gated Group Communications';
// Comms gate fixes: no promise that everyone can read it (the default Limited privacy hides a colleague's conversation).
export const GATED_SETTING_DESCRIPTION = 'When on, the first staff member to engage a conversation owns it. Others can\'t reply until they ask to join, are invited, or it\'s released. What they can see still follows your privacy settings.';
export const GATED_SETTING_PRIVACY_NOTE = 'Each person\'s conversation privacy choice still decides how much colleagues can see of a conversation they are not in.';
export const DEFAULT_GREETING_HOURS = Object.freeze({ afternoon: 12, evening: 17 });

// Comms gate fixes: fail open ONLY when the database says the gate's own function or table does not exist (migration 284 / 286
// not applied yet, so the gate cannot be on). Both parts must match: a "missing object" error code AND a gate object's name.
// Anything else (a timeout, a permission error, a different missing object, a thrown network failure) fails closed.
const MISSING_OBJECT_CODES = new Set(['PGRST202', 'PGRST205', '42883', '42P01']);
const GATE_OBJECT_NAME = /\b(?:public\.)?(?:conv_gate_claim_send|conv_gate_check_address|conv_gate_unclaim|conv_gate_status|conv_gate_timeline|conv_release|org_comms_group_settings)\b/;

/** True only when an error means the gate's migration is not applied yet (the gate cannot be on before it is). */
export function isGateUnavailableError(error) {
    if (!error || typeof error !== 'object') return false;
    const code = String(error.code || '').trim();
    if (!MISSING_OBJECT_CODES.has(code)) return false;
    return GATE_OBJECT_NAME.test(`${error.message || ''} ${error.details || ''} ${error.hint || ''}`);
}

/** The words a refused sender sees. */
export function ownedByMessage(ownerName) {
    const name = String(ownerName || '').trim() || 'A colleague';
    return `${name} owns this conversation. Ask to join before you reply. Your message has not been sent.`;
}

/**
 * Server side (service-role client): may this person send into this conversation? With the gate on, an unowned conversation is
 * claimed for them (claim = true) atomically. Returns { allowed, gated, took } or { allowed: false, status, code, ownerName, error }.
 * A send with no conversation id goes through checkConversationGateByAddress instead.
 * @param {any} serviceClient
 * @param {{ sessionId?: string, userId?: string, claim?: boolean }} [options]
 * @returns {Promise<{ allowed: boolean, gated?: boolean, took?: boolean, sessionId?: string, unavailable?: boolean, status?: number, code?: string, ownerName?: string, error?: string }>}
 */
export async function checkConversationGate(serviceClient, { sessionId = '', userId = '', claim = true } = {}) {
    if (!sessionId) return { allowed: true, gated: false, took: false };
    let result;
    try {
        result = await serviceClient.rpc('conv_gate_claim_send', { p_session: sessionId, p_user: userId, p_claim: Boolean(claim) });
    } catch (failure) {
        result = { data: null, error: failure };
    }
    const { data, error } = result || {};
    if (error) {
        if (isGateUnavailableError(error)) return { allowed: true, gated: false, took: false, unavailable: true };
        return { allowed: false, status: 500, code: 'gate_check_failed', error: 'Could not check who owns this conversation. Please try again.' };
    }
    if (data?.allowed === true) return { allowed: true, gated: Boolean(data.gated), took: Boolean(data.took), sessionId };
    return refusal(data);
}

function refusal(data) {
    const code = data?.code || GATE_OWNED_CODE;
    const status = code === 'not_found' ? 404 : (code === 'bad_request' ? 400 : 403);
    return {
        allowed: false,
        status,
        code,
        sessionId: data?.session_id || '',
        ownerName: data?.owner_name || '',
        error: code === GATE_OWNED_CODE ? ownedByMessage(data?.owner_name) : (data?.error || 'You cannot reply in this conversation.'),
    };
}

/**
 * Server side: a send that starts without a conversation in hand (New message, a campaign, click-to-call from a CRM account).
 * The database finds this client's conversation in the company by address and applies the gate (migration 286), so a
 * colleague's conversation the sender cannot even see is still protected. claim = false for campaigns (check only).
 * channels: ['sms','mms'], ['whatsapp'], ['email'], or null for any (a call).
 * @param {any} serviceClient
 * @param {{ orgId?: string, userId?: string, address?: string, channels?: string[] | null, claim?: boolean }} [options]
 * @returns {Promise<{ allowed: boolean, gated?: boolean, took?: boolean, sessionId?: string, unavailable?: boolean, status?: number, code?: string, ownerName?: string, error?: string }>}
 */
export async function checkConversationGateByAddress(serviceClient, { orgId = '', userId = '', address = '', channels = null, claim = true } = {}) {
    if (!orgId || !String(address || '').trim()) return { allowed: true, gated: false, took: false };
    let result;
    try {
        result = await serviceClient.rpc('conv_gate_check_address', {
            p_org: orgId, p_user: userId, p_address: String(address).trim(), p_channels: channels, p_claim: Boolean(claim),
        });
    } catch (failure) {
        result = { data: null, error: failure };
    }
    const { data, error } = result || {};
    if (error) {
        if (isGateUnavailableError(error)) return { allowed: true, gated: false, took: false, unavailable: true };
        return { allowed: false, status: 500, code: 'gate_check_failed', error: 'Could not check who owns this client\'s conversation. Please try again.' };
    }
    if (data?.allowed === true) return { allowed: true, gated: Boolean(data.gated), took: Boolean(data.took), sessionId: data.session_id || '' };
    return refusal(data);
}

/**
 * Server side: the provider refused a message or call that had just claimed the conversation (took = true). Give the claim back
 * so a failed send never leaves its sender owning the conversation. Only the claim that send made is undone (migration 286).
 * Never throws: a failure here is logged, and the send's own error is what the person sees.
 * @param {any} serviceClient
 * @param {{ sessionId?: string, userId?: string, reason?: string }} [options]
 * @returns {Promise<boolean>}
 */
export async function releaseGateClaim(serviceClient, { sessionId = '', userId = '', reason = 'send_failed' } = {}) {
    if (!sessionId || !userId) return false;
    try {
        const { data, error } = await serviceClient.rpc('conv_gate_unclaim', { p_session: sessionId, p_user: userId, p_reason: reason });
        if (error) {
            console.error('conversation gate: could not give the claim back after a failed send', error.message || error);
            return false;
        }
        return data === true;
    } catch (failure) {
        console.error('conversation gate: could not give the claim back after a failed send', failure?.message || failure);
        return false;
    }
}

export const DEFAULT_GATE_STATUS = Object.freeze({
    found: false, gated: false, owned: false, ownerId: null, ownerName: '', iAmIn: false, iAmOwner: false,
    participants: 0, joinPending: false, canReply: true, canRelease: false, loaded: false,
});

export function normalizeGateStatus(raw) {
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_GATE_STATUS };
    return {
        found: raw.found === true,
        gated: raw.gated === true,
        owned: raw.owned === true,
        ownerId: raw.owner_id || null,
        ownerName: raw.owner_name || '',
        iAmIn: raw.i_am_in === true,
        iAmOwner: raw.i_am_owner === true,
        participants: Number(raw.participants) || 0,
        joinPending: raw.join_pending === true,
        canReply: raw.can_reply !== false,
        canRelease: raw.can_release === true,
        loaded: true,
    };
}

/** Browser: what the screen shows for one conversation. Before migration 284 everything reads as "not gated". */
export async function loadGateStatus(client, sessionId) {
    if (!client || !sessionId || typeof client.rpc !== 'function') return { ...DEFAULT_GATE_STATUS };
    try {
        const { data, error } = await client.rpc('conv_gate_status', { p_session: sessionId });
        if (error) return { ...DEFAULT_GATE_STATUS, loaded: true, error: isGateUnavailableError(error) ? '' : (error.message || '') };
        return normalizeGateStatus(data);
    } catch {
        return { ...DEFAULT_GATE_STATUS, loaded: true };
    }
}

export async function releaseConversation(client, { sessionId }) {
    const { data, error } = await client.rpc('conv_release', { p_session: sessionId });
    if (error) throw new Error(error.message || 'Could not release this conversation.');
    return data;
}

/**
 * Comms gate fixes: what a person sees when a link opens a conversation they can't load. It no longer blames the owner's privacy
 * setting for every case: the conversation may be out of their list for privacy, for their area, or because it is owned under
 * Gated Group Communications. Pure.
 */
export function openNoticeText(status = {}) {
    const name = String(status?.ownerName || '').trim();
    if (status?.found && status?.owned && name) {
        return status.gated
            ? `${name} owns this conversation, so it isn't in your list. Ask ${name} to invite you, or ask an admin to release it.`
            : `${name} has this conversation, so it isn't in your list. Ask ${name} to invite you.`;
    }
    return 'This conversation is not available to you. It may have been deleted, or it belongs to a colleague or an area you can\'t open.';
}

/** One line per audit event, in plain words. */
export function gateTimelineText(row = {}) {
    const actor = String(row.actor_name || '').trim() || 'A colleague';
    const subject = String(row.subject_name || '').trim() || actor;
    switch (row.event) {
    case 'taken': return `${subject} took this conversation`;
    case 'requested': return `${actor} asked to join`;
    case 'joined': return `${subject} joined`;
    case 'released': {
        if (!row.auto) return `Released by ${actor}`;
        if (row.reason === 'send_failed') return 'Released automatically because the message did not send';
        if (row.reason === 'call_failed') return 'Released automatically because the call did not go through';
        if (row.reason === 'gate_off') return 'Released when Gated Group Communications was turned off';
        return 'Released automatically when the conversation closed';
    }
    default: return '';
    }
}

export async function loadGateTimeline(client, sessionId) {
    if (!client || !sessionId || typeof client.rpc !== 'function') return [];
    try {
        const { data, error } = await client.rpc('conv_gate_timeline', { p_session: sessionId });
        if (error || !Array.isArray(data)) return [];
        return data
            .map((row) => ({ id: row.id, event: row.event, at: row.created_at, text: gateTimelineText(row) }))
            .filter((row) => row.text);
    } catch {
        return [];
    }
}

// ---- the company setting (Communications settings > Group communications) ----
export function normalizeGroupSettings(row = {}) {
    const afternoon = Number.parseInt(row?.greeting_afternoon_hour, 10);
    const evening = Number.parseInt(row?.greeting_evening_hour, 10);
    const validHours = Number.isFinite(afternoon) && Number.isFinite(evening) && afternoon >= 1 && evening <= 23 && afternoon < evening;
    return {
        gatedGroupComms: row?.gated_group_comms === true,
        greetingAfternoonHour: validHours ? afternoon : DEFAULT_GREETING_HOURS.afternoon,
        greetingEveningHour: validHours ? evening : DEFAULT_GREETING_HOURS.evening,
    };
}

export async function loadGroupSettings(client, orgId) {
    const fallback = { ...normalizeGroupSettings({}), available: true };
    if (!client || !orgId || typeof client.from !== 'function') return fallback;
    try {
        const { data, error } = await client.from('org_comms_group_settings')
            .select('gated_group_comms, greeting_afternoon_hour, greeting_evening_hour')
            .eq('org_id', orgId)
            .maybeSingle();
        if (error) return { ...fallback, available: !isGateUnavailableError(error), error: isGateUnavailableError(error) ? '' : error.message };
        return { ...normalizeGroupSettings(data || {}), available: true };
    } catch {
        return fallback;
    }
}

export function validateGreetingHours(afternoon, evening) {
    const a = Number.parseInt(afternoon, 10);
    const e = Number.parseInt(evening, 10);
    if (!Number.isFinite(a) || !Number.isFinite(e)) return 'Choose both times.';
    if (a < 1 || a > 22 || e < 2 || e > 23) return 'Choose whole hours between 1:00 and 23:00.';
    if (a >= e) return 'Afternoon must start before evening.';
    return '';
}

export async function saveGroupSettings(client, { orgId, gatedGroupComms, greetingAfternoonHour, greetingEveningHour }) {
    const problem = validateGreetingHours(greetingAfternoonHour, greetingEveningHour);
    if (problem) throw new Error(problem);
    const { error } = await client.from('org_comms_group_settings').upsert({
        org_id: orgId,
        gated_group_comms: Boolean(gatedGroupComms),
        greeting_afternoon_hour: Number.parseInt(greetingAfternoonHour, 10),
        greeting_evening_hour: Number.parseInt(greetingEveningHour, 10),
    }, { onConflict: 'org_id' });
    if (error) {
        if (/row-level security|42501|permission/i.test(`${error.code || ''} ${error.message || ''}`)) {
            throw new Error('Only an Owner or Tenant Admin can change Group communications.');
        }
        throw new Error(error.message || 'Could not save Group communications.');
    }
}

/**
 * Put the gate events (take / ask / join / release) into the conversation as centred system lines, each before the first
 * message that came after it. Dividers stay where they are. Pure.
 */
export function mergeGateEvents(items = [], events = []) {
    const lines = (Array.isArray(events) ? events : [])
        .map((event) => ({
            id: `gate-${event.id}`,
            kind: 'gate',
            direction: 'system',
            systemLine: true,
            systemKind: 'gate',
            text: event.text,
            at: Date.parse(event.at || '') || null,
        }))
        .filter((line) => line.text && line.at !== null)
        .sort((left, right) => left.at - right.at);
    if (!lines.length) return items;
    const out = [];
    let next = 0;
    (Array.isArray(items) ? items : []).forEach((item) => {
        const at = typeof item?.at === 'number' ? item.at : null;
        while (next < lines.length && at !== null && item?.kind !== 'divider' && lines[next].at <= at) {
            out.push(lines[next]);
            next += 1;
        }
        out.push(item);
    });
    while (next < lines.length) { out.push(lines[next]); next += 1; }
    return out;
}
